/* Global imports. */
import React, { useState, useEffect, useRef } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

/* Component imports. */
import Banner from "@components/Banner";

/* Model imports. */
import KontentBanner from "@models/kontent/KontentBanner";

interface Props {
    banners: [KontentBanner];
    carouselButtonColour: string;
    carouselControlsBackgroundColour: string;
    autoplay: boolean;
}

function BannerCarousel({ banners, carouselButtonColour, carouselControlsBackgroundColour, autoplay }: Props) {

    const [oldSlide, setOldSlide] = useState(0);
    const [activeSlide, setActiveSlide] = useState(0);
    const [auto_play, setAutoplay] = useState(autoplay);

    let sliderRef = useRef(null);
    const sliderContainRef = useRef(null);

    const next = () => {
        sliderRef.slickNext();

    };
    const previous = () => {
        sliderRef.slickPrev();
    };

    const play_pause = () => {
        if (auto_play) {
            sliderRef.slickPause();
            setAutoplay(false);
        } else {
            sliderRef.slickPlay();
            setAutoplay(true);
        }
    };

    const settings = {
        dots: false,
        arrows: false,
        infinite: true,
        speed: 750,
        beforeChange: (current, next) => {
            setOldSlide(current);
            setActiveSlide(next);
        },
        autoplay: autoplay,
    };

    const sliderDots = () => {
        if (banners.length > 1) {
            return (
                <div className="carousel-controls__dots gap-2 flex flex-row">
                    {banners.map((item: any, index: number) => {
                        return (
                            <button
                                key={index}
                                className={
                                    "carousel-controls__dot w-3 h-3 rounded-full border" +
                                    (index === 0 ? " bg-" + carouselButtonColour : " bg-transparent")
                                }
                                style={{ borderColor: carouselButtonColour }}
                                onClick={() => sliderRef.slickGoTo(index)}
                                data-index={index}
                            ></button>
                        );
                    })}
                </div>
            );
        }
    };

    useEffect(() => {
        if (sliderContainRef.current) {
            const dots = sliderContainRef.current.querySelectorAll(".carousel-controls__dot");
            dots.forEach((dot: any, index: number) => {
                if (index === activeSlide) {
                    dot.classList.add("bg-" + carouselButtonColour);
                    dot.classList.remove("bg-transparent");
                } else {
                    dot.classList.remove("bg-" + carouselButtonColour);
                    dot.classList.add("bg-transparent");
                }
            });

            const slider = sliderContainRef.current.querySelector(".banner-carousel");
            if (slider) {
                if (activeSlide === 0) {
                    // hide prev button
                    slider.classList.add("first-slide");
                } else if (activeSlide === banners.length - 1) {
                    // hide next button
                    slider.classList.add("last-slide");
                }
            }
        }
    }, [activeSlide]);

    return (
        <section className="w-full relative shadow-card mb-8" ref={sliderContainRef}>
            <Slider
                {...settings}
                className="banner-carousel"
                ref={(slider) => {
                    sliderRef = slider;
                }}
            >
                {banners.map((item: any, key) => {
                    const banner = item.elements;
                    const styling = banner.styling.value[0] && banner.styling.value[0].elements;
                    const primaryTextColor =
                        styling && styling.primary_colour.value ? styling.primary_colour.value : ``;
                    const backgroundColour =
                        styling && styling.background_colour.value ? styling.background_colour.value : ``;

                    return (
                        <Banner
                            title={banner.title.value}
                            text={banner.text.value}
                            buttonText={banner.button_text && banner.button_text.value}
                            buttonUrl={banner.button_url && banner.button_url.value}
                            imageUrl={
                                banner.background_image &&
                                banner.background_image.value &&
                                banner.background_image.value[0] &&
                                banner.background_image.value[0].url
                            }
                            imageLinkURL={banner.background_image_link && banner.background_image_link.value  ? banner.background_image_link.value : '' }
                            textPlacementClass={
                                banner.text_placement && banner.text_placement.value && banner.text_placement.value[0]
                                    ? `banner_text_${banner.text_placement.value[0].codename}`
                                    : `banner_text_left`
                            }
                            primaryTextColour={primaryTextColor}
                            backgroundColour={
                                styling && styling.background_colour.value ? styling.background_colour.value : ``
                            }
                            backgroundColourLeft={
                                banner.background_colour_left && banner.background_colour_left.value
                                    ? banner.background_colour_left.value
                                    : backgroundColour && backgroundColour != "#CCCCCC"
                                    ? backgroundColour
                                    : `transparent`
                            }
                            backgroundColourRight={
                                banner.background_colour_right && banner.background_colour_right.value
                                    ? banner.background_colour_right.value
                                    : backgroundColour && backgroundColour != "#CCCCCC"
                                    ? backgroundColour
                                    : `transparent`
                            }
                            isPageHero={
                                !!(
                                    banner.page_hero &&
                                    banner.page_hero.value &&
                                    banner.page_hero.value[0] &&
                                    banner.page_hero.value[0].codename === "yes"
                                )
                            }
                            bannerWidth={
                                banner.width &&
                                banner.width.value &&
                                banner.width.value[0] &&
                                banner.width.value[0].codename
                            }
                            bannerType={
                                banner.type && banner.type.value && banner.type.value[0]
                                    ? banner.type.value[0].codename
                                    : "background_image"
                            }
                            imagePlacement={
                                banner.background_image_placement &&
                                banner.background_image_placement.value &&
                                banner.background_image_placement.value[0]
                                    ? banner.background_image_placement.value[0].codename
                                    : "left"
                            }
                            key={key}
                        />
                    );
                })}
            </Slider>

            <div
                className="carousel-controls flex flex-row justify-center items-center py-3 gap-6"
                style={{ backgroundColor: carouselControlsBackgroundColour }}
            >
                <button
                    className="carousel-controls__button carousel-controls__button--left border border-black rounded-full h-8 w-8 flex justify-center items-center group"
                    style={{ borderColor: carouselButtonColour }}
                    onClick={previous}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="group-hover:opacity-60">
                        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12l4.58-4.59z" fill={carouselButtonColour} />
                    </svg>
                </button>
                {sliderDots()}
                <button
                    className="carousel-controls__button carousel-controls__button--right rotate-180 border border-black rounded-full h-8 w-8 flex justify-center items-center group"
                    style={{ borderColor: carouselButtonColour }}
                    onClick={next}
                >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="group-hover:opacity-60">
                        <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12l4.58-4.59z" fill={carouselButtonColour} />
                    </svg>
                </button>
                <button
                    className="carousel-controls__button carousel-controls__button--last-slide border border-black rounded-full h-8 w-8 flex justify-center items-center group"
                    style={{ borderColor: carouselButtonColour }}
                    onClick={play_pause}
                >
                    {auto_play ? 
                        (
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none" className="group-hover:opacity-60">
                                <path d="M4.16646 16C3.78876 16 3.47226 15.872 3.21716 15.616C2.96116 15.3609 2.83316 15.0444 2.83316 14.6667V1.33333C2.83316 0.955556 2.96116 0.638667 3.21716 0.382667C3.47226 0.127556 3.78876 0 4.16646 0C4.54426 0 4.86076 0.127556 5.11586 0.382667C5.37186 0.638667 5.49986 0.955556 5.49986 1.33333V14.6667C5.49986 15.0444 5.37186 15.3609 5.11586 15.616C4.86076 15.872 4.54426 16 4.16646 16Z" fill={carouselButtonColour}/>
                                <path d="M9.83345 16C9.45575 16 9.13925 15.872 8.88415 15.616C8.62815 15.3609 8.50015 15.0444 8.50015 14.6667V1.33333C8.50015 0.955556 8.62815 0.638667 8.88415 0.382667C9.13925 0.127556 9.45575 0 9.83345 0C10.2113 0 10.5278 0.127556 10.7829 0.382667C11.0389 0.638667 11.1669 0.955556 11.1669 1.33333V14.6667C11.1669 15.0444 11.0389 15.3609 10.7829 15.616C10.5278 15.872 10.2113 16 9.83345 16Z" fill={carouselButtonColour}/>
                            </svg>

                        ) : 
                        (
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none" className="group-hover:opacity-60">
                                <path d="M2.69506 15.6054C2.43198 15.3423 2.30045 15.0075 2.30045 14.6009C2.30045 14.1943 2.43198 13.8595 2.69506 13.5964L8.29148 8L2.69506 2.40359C2.43198 2.14051 2.30045 1.80568 2.30045 1.3991C2.30045 0.992526 2.43198 0.657697 2.69506 0.394618C2.95814 0.131539 3.29297 0 3.69955 0C4.10613 0 4.44096 0.131539 4.70404 0.394618L11.3049 6.99552C11.4484 7.13901 11.5503 7.29447 11.6106 7.46188C11.6699 7.6293 11.6995 7.80867 11.6995 8C11.6995 8.19133 11.6699 8.3707 11.6106 8.53812C11.5503 8.70553 11.4484 8.86099 11.3049 9.00448L4.70404 15.6054C4.44096 15.8685 4.10613 16 3.69955 16C3.29297 16 2.95814 15.8685 2.69506 15.6054Z" fill={carouselButtonColour}/>
                            </svg>
                        )
                    }
                </button>
            </div>
        </section>
    );
}

export default BannerCarousel;
